import axios from "axios";
import { DEFAULT_TENANT_CONFIG, TenantConfig } from "lib/tenant/types";
import { convertParamsToString } from "utils/helpers";
import { getClientRef } from "./clientRef";
import { subRequest } from "./request";

const endpointUrl = process.env.REACT_APP_ENDPOINT_URL;
  if (!endpointUrl) {
    throw new Error("Missing REACT_APP_ENDPOINT_URL (API base URL). Configure it in environment/secrets.");
  }
  export const baseURL = endpointUrl;
const tenantLookupKey = process.env.REACT_APP_TENANT_LOOKUP_KEY || "";
const basicAuth = process.env.REACT_APP_AUTH;
 if (!basicAuth) {
   throw new Error("Missing REACT_APP_AUTH (Basic authentication credentials). Configure it in environment/secrets.");
 }

export function getRootDomain(hostname: string): string {
  const host = hostname.split(":")[0];

  if (host === "localhost" || host === "127.0.0.1") {
    return process.env.REACT_APP_TENANT_LOOKUP_DOMAIN || host;
  }

  const withoutWww = host.startsWith("www.") ? host.slice(4) : host;
  const parts = withoutWww.split(".");

  if (parts.length >= 2) {
    return parts.slice(-2).join(".");
  }

  return withoutWww;
}

export function isTenantLookupDomain(hostname = window.location.hostname): boolean {
  const lookupDomain = process.env.REACT_APP_TENANT_LOOKUP_DOMAIN || "";
  return Boolean(lookupDomain) && getRootDomain(hostname) === lookupDomain;
}

let tenantConfigPromise: Promise<TenantConfig> | null = null;

function mapTenantResponse(data: any): TenantConfig {
  const tenant = data?.data;

  const webAppUrl =
    tenant?.webAppUrl || tenant?.webAppDomain || tenant?.appUrl || undefined;

  return {
    id: tenant?.id != null ? String(tenant.id) : DEFAULT_TENANT_CONFIG.id,
    name: tenant?.name || DEFAULT_TENANT_CONFIG.name,
    primaryColor: tenant?.primaryColor || DEFAULT_TENANT_CONFIG.primaryColor,
    secondaryColor:
      tenant?.secondaryColor || DEFAULT_TENANT_CONFIG.secondaryColor,
    primaryDomain: tenant?.primaryDomain || undefined,
    logoUrl: tenant?.logoUrl,
    favicon: tenant?.configImages?.favicon,
    webAppUrl: typeof webAppUrl === "string" && webAppUrl.trim() ? webAppUrl.trim() : undefined,
  };
}

export function fetchTenantConfig(): Promise<TenantConfig> {
  if (tenantConfigPromise) {
    return tenantConfigPromise;
  }

  tenantConfigPromise = (async () => {
    if (typeof window === "undefined") {
      return DEFAULT_TENANT_CONFIG;
    }

    if (!tenantLookupKey) {
      console.warn("[tenant] REACT_APP_TENANT_LOOKUP_KEY is not set");
      return DEFAULT_TENANT_CONFIG;
    }

    const domain = getRootDomain(window.location.hostname);
    const path = convertParamsToString(subRequest.TENANT_BY_DOMAIN, { domain });
    const url = `${baseURL}${path}`;

    try {
      const { data } = await axios.get(url, {
        headers: {
          Accept: "application/json",
          Authorization: basicAuth,
          "X-Tenant-Lookup-Key": tenantLookupKey,
          "X-Client-Ref": getClientRef(),
        },
      });

      return mapTenantResponse(data);
    } catch (error) {
      console.error("[tenant] Failed to fetch tenant config", error);
      return DEFAULT_TENANT_CONFIG;
    }
  })();

  return tenantConfigPromise;
}

export async function fetchTenantId(): Promise<string> {
  const tenant = await fetchTenantConfig();
  return tenant.id;
}
